import { type HeadingAnchor, headingFragment, headingKeys } from "@esi/shared";
import {
  Fragment,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { DiagramFailed, MermaidDiagram } from "@/components/diagram/Diagram";
import { WideBlock } from "@/components/wide-block/WideBlock";
import { sourceKey } from "@/conversation/artefacts/mermaid";
import { cn } from "@/lib/utils";
import { Link } from "../../router.tsx";
import { type Block, type Inline, parseMarkdown, plainText } from "./parse.ts";

export { plainText };

/**
 * Bounds on the `mermaid` fences a document draws (#380), checked before any frame is created: a
 * bigger fence, or one past the first `diagrams`, shows as code with a note.
 */
export const MARKDOWN_DIAGRAM_LIMITS = { chars: 20_000, lines: 500, diagrams: 10 } as const;

/** A raw-offset range the caller marks (unsourced or unverified statements in an answer). */
export interface Decoration<K extends string = string> {
  start: number;
  end: number;
  kind: K;
}

export interface RenderOptions {
  /**
   * Stable article heading anchors, `${prefix}-${fragment}` by the heading's text and occurrence
   * (`headingFragment`); omitted in chat and previews.
   */
  headingPrefix?: string;
  /** Omit a duplicate article title without changing the original heading indices. */
  omitTitleHeading?: string;
  /** The in-app href of the page a `[[Title]]` link names, or null (rendered as plain text). */
  resolve?: (title: string) => string | null;
  /**
   * Opens a resolved `[[Title]]` link in place (the conversation's wiki panel) on a plain click;
   * the link keeps its href for a new tab or a copied address.
   */
  openWikiLink?: (href: string) => void;
  /** Renders an atom the parser was given (a citation chip); its raw text otherwise. */
  renderAtom?: (atom: { raw: string; start: number; end: number }, key: string) => ReactNode;
  /** Ranges to wrap; text leaves are cut at their edges, so emphasis inside is marked too. */
  decorations?: readonly Decoration[];
  /**
   * Wraps one piece of a decorated range. `last` is true on the range's final piece, where an
   * announcement for assistive technology belongs (once per range).
   */
  renderDecoration?: (kind: string, children: ReactNode, key: string, last: boolean) => ReactNode;
  /**
   * Shows a link's host after its label when the label does not already say it: model output
   * must not hide where a link goes (answers).
   */
  showLinkHost?: boolean;
  /** Level of a `#` heading: 2 on wiki pages, 3 inside an answer (clamped to 6). */
  headingBase?: 2 | 3;
  /**
   * Draws closed `mermaid` fences as diagrams (default). Off where the text is not one real
   * document: the review's Marked view merges removed and added lines into each fence.
   */
  drawDiagrams?: boolean;
  /** Beside each anchored heading (`headingPrefix`), outside it: the page-thread affordance. */
  headingAside?: ((anchor: HeadingAnchor) => ReactNode) | undefined;
  /**
   * Nodes rendered inline right after a raw offset (the review's markers at the end of a
   * passage). Inside a link, code or `[[link]]` they follow the whole node.
   */
  inlineAt?: ReadonlyMap<number, ReactNode> | undefined;
  /** Nodes rendered after a top-level block, by its index (the review's blocking lines). */
  blockAfter?: ReadonlyMap<number, ReactNode> | undefined;
}

/**
 * Safe Markdown for wiki pages and drafts: React elements only (never HTML), so raw HTML in a
 * page shows as text; links kept for `http(s)` and `mailto`; `[[Title]]` links resolved through
 * `resolve` to pages the viewer can read, plain text otherwise.
 */
export function Markdown(props: { markdown: string } & RenderOptions) {
  const { markdown, ...options } = props;
  const blocks = useMemo(() => parseMarkdown(markdown), [markdown]);
  return <MarkdownBlocks blocks={blocks} {...options} />;
}

export function MarkdownBlocks(
  props: { blocks: readonly Block[]; className?: string } & RenderOptions,
) {
  const { t } = useTranslation();
  const { blocks, className, ...options } = props;
  const lastPiece = useMemo(
    () => lastPieces(blocks, options.decorations ?? []),
    [blocks, options.decorations],
  );
  // Links open in a new tab and say so as their description (their name stays the label).
  const newTab = useId();
  const headingIds = useMemo(
    () =>
      new Map(
        options.headingPrefix
          ? headingKeys(blocks).map((h) => [
              h.block,
              { id: `${options.headingPrefix}-${headingFragment(h)}`, anchor: h },
            ])
          : [],
      ),
    [blocks, options.headingPrefix],
  );
  const draw = options.drawDiagrams !== false;
  const diagrams = useMemo(() => (draw ? diagramOrdinals(blocks) : new Map()), [blocks, draw]);
  const labels = {
    table: t("wideBlock.table"),
    code: t("wideBlock.code"),
    expandTable: t("wideBlock.expandTable"),
    expandCode: t("wideBlock.expandCode"),
    diagram: t("wideBlock.diagram"),
    expandDiagram: t("wideBlock.expandDiagram"),
  };
  const ctx: Ctx = { ...options, lastPiece, newTab, headingIds, labels, diagrams };
  const omittedHeading = titleHeadingIndex(blocks, options.omitTitleHeading);
  return (
    <div
      className={cn("flex min-w-0 flex-col gap-3.5 text-[15px] leading-[1.7] text-fg", className)}
    >
      <span id={newTab} hidden>
        {t("app.newTab")}
      </span>
      {blocks.map((block, i) =>
        i === omittedHeading ? null : (
          // biome-ignore lint/suspicious/noArrayIndexKey: blocks only grow at the end (streaming)
          <Fragment key={i}>
            {renderBlock(block, `${i}`, ctx)}
            {options.blockAfter?.get(i)}
          </Fragment>
        ),
      )}
    </div>
  );
}

interface Ctx extends RenderOptions {
  /** Per decoration index: the raw end of its last rendered piece. */
  lastPiece: Map<number, number>;
  /** ID of the "opens in a new tab" description. */
  newTab: string;
  /** Per top-level block index: its heading's anchor id and key. */
  headingIds: Map<number, { id: string; anchor: HeadingAnchor }>;
  /** Names of the wide blocks (tables, code and diagrams) and their Expand buttons. */
  labels: {
    table: string;
    code: string;
    expandTable: string;
    expandCode: string;
    diagram: string;
    expandDiagram: string;
  };
  /** Per top-level block index of a closed `mermaid` fence: its rank among them (0-based). */
  diagrams: Map<number, number>;
}

/** The closed `mermaid` fences of a document, in order. */
function diagramOrdinals(blocks: readonly Block[]): Map<number, number> {
  const ranks = new Map<number, number>();
  blocks.forEach((block, i) => {
    if (block.type === "code" && block.lang === "mermaid" && block.closed) {
      ranks.set(i, ranks.size);
    }
  });
  return ranks;
}

/** Why a closed `mermaid` fence is not drawn, or null when it may be. */
function diagramRefusal(text: string, rank: number): "tooLarge" | "tooMany" | null {
  if (rank >= MARKDOWN_DIAGRAM_LIMITS.diagrams) {
    return "tooMany";
  }
  const lines = text.split("\n").length;
  return text.length > MARKDOWN_DIAGRAM_LIMITS.chars || lines > MARKDOWN_DIAGRAM_LIMITS.lines
    ? "tooLarge"
    : null;
}

const headingClass = (depth: number) =>
  depth <= 2 ? "mt-2 text-lg font-semibold tracking-tight" : "mt-1 text-base font-semibold";

function renderBlock(block: Block, key: string, ctx: Ctx): ReactNode {
  switch (block.type) {
    case "heading": {
      // Clamped below the context's own heading, so the page outline stays valid.
      const Tag = `h${Math.min(6, Math.max(ctx.headingBase ?? 2, block.depth))}` as "h2";
      const anchored = ctx.headingIds.get(Number(key));
      const heading = (
        <Tag id={anchored?.id} className={cn(headingClass(block.depth), "scroll-mt-6")}>
          {inlines(block.content, key, ctx)}
        </Tag>
      );
      const aside = anchored && ctx.headingAside?.(anchored.anchor);
      return aside ? (
        <div className="group relative flex items-start justify-between gap-2">
          {heading}
          <div className={cn("shrink-0", block.depth <= 2 ? "mt-2" : "mt-1")}>{aside}</div>
        </div>
      ) : (
        heading
      );
    }
    case "paragraph":
      return <p className="break-words">{inlines(block.content, key, ctx)}</p>;
    case "quote":
      return (
        <blockquote className="border-l-2 border-line-strong pl-3.5 text-fg-secondary">
          {inlines(block.content, key, ctx)}
        </blockquote>
      );
    case "code": {
      const rank = ctx.diagrams.get(Number(key));
      if (rank !== undefined) {
        return (
          <FenceDiagram
            source={block.text}
            rank={rank}
            code={codeBlock(block.text, ctx)}
            label={ctx.labels.diagram}
            expandLabel={ctx.labels.expandDiagram}
          />
        );
      }
      return codeBlock(block.text, ctx);
    }
    case "rule":
      return <hr className="border-line" />;
    case "list":
      return renderList(block, key, ctx);
    case "table":
      return (
        <WideBlock
          label={ctx.labels.table}
          expandLabel={ctx.labels.expandTable}
          fit="min-content"
          className="rounded-lg border border-line bg-surface-raised"
        >
          <table className="w-full border-collapse text-sm leading-normal">
            <thead>
              <tr className="border-b border-line">
                {block.header.map((cell, c) => (
                  <th
                    // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                    key={c}
                    scope="col"
                    className={cn(
                      CELL_MIN,
                      "px-3 py-2 text-left align-bottom text-xs font-medium text-fg-muted",
                    )}
                    style={alignStyle(block.align[c])}
                  >
                    {inlines(cell, `${key}-h${c}`, ctx)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {block.rows.map((row, r) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows only grow at the end
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                      key={c}
                      className={cn(CELL_MIN, "px-3 py-2 align-top")}
                      style={alignStyle(block.align[c])}
                    >
                      {inlines(cell, `${key}-${r}-${c}`, ctx)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </WideBlock>
      );
  }
}

/** How long a fence's source must stay unchanged before a new version of it is drawn. */
const SETTLE_MS = 400;

/** `value`, as it was once it stopped changing for `ms` (the first value at once). */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (Object.is(value, settled)) {
      return;
    }
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, settled, ms]);
  return settled;
}

/** A fenced code block: a wide block of preformatted text. */
function codeBlock(text: string, ctx: Ctx): ReactNode {
  return (
    <WideBlock
      label={ctx.labels.code}
      expandLabel={ctx.labels.expandCode}
      className="rounded-lg border border-line bg-surface-sunken"
      // On the scroller, so the inline-end padding stays when scrolled to the end.
      contentClassName="p-3.5"
    >
      <pre className="font-mono text-[13px] leading-normal">
        <code>{text}</code>
      </pre>
    </WideBlock>
  );
}

/**
 * A closed `mermaid` fence (#380), drawn like a Mermaid artefact: in the sandboxed frame, shown as
 * a sanitised image in a wide block (Fit / Actual size, Expand with zoom). Over the limits, or
 * when it cannot be drawn, its code block with a short note.
 */
function FenceDiagram(props: {
  source: string;
  rank: number;
  code: ReactNode;
  label: string;
  expandLabel: string;
}) {
  const { t } = useTranslation();
  // Typed in an editor's preview: drawn once the fence stops changing, not on every key.
  const source = useSettled(props.source, SETTLE_MS);
  // Both the text shown and the one about to be drawn must be within the limits.
  const refusal = diagramRefusal(props.source, props.rank) ?? diagramRefusal(source, props.rank);
  const fallback = (note?: string) => (
    <DiagramFailed code={props.code} {...(note ? { note } : {})} />
  );
  if (refusal === "tooLarge") {
    return fallback(t("wideBlock.diagramTooLarge"));
  }
  if (refusal === "tooMany") {
    return fallback(t("wideBlock.diagramTooMany", { count: MARKDOWN_DIAGRAM_LIMITS.diagrams }));
  }
  return (
    <MermaidDiagram
      cacheKey={sourceKey(source)}
      source={source}
      title={props.label}
      expandLabel={props.expandLabel}
      failed={fallback()}
    />
  );
}

/**
 * A table cell's narrowest width once the table is at its widest (#379): 7rem holds a date, a
 * part number or two short words at 14px, so cells stop wrapping word by word and the table
 * scrolls instead; below it, at its natural width, a column is as wide as its content.
 */
const CELL_MIN = "min-w-28";

const alignStyle = (align: "left" | "center" | "right" | null | undefined) =>
  align ? { textAlign: align } : undefined;

function renderList(block: Block & { type: "list" }, key: string, ctx: Ctx): ReactNode {
  const List = block.ordered ? "ol" : "ul";
  return (
    <List
      start={block.ordered && block.start !== 1 ? block.start : undefined}
      className={cn(block.ordered ? "list-decimal" : "list-disc", "flex flex-col gap-1 pl-5")}
    >
      {block.items.map((item, n) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: items only grow at the end
        <li key={n} className="pl-0.5">
          {inlines(item.content, `${key}-${n}`, ctx)}
          {item.children.map((child, c) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: nested lists only grow at the end
            <div key={c} className="mt-1">
              {renderList(child, `${key}-${n}c${c}`, ctx)}
            </div>
          ))}
        </li>
      ))}
    </List>
  );
}

function inlines(nodes: readonly Inline[], key: string, ctx: Ctx): ReactNode[] {
  return nodes.map((node, i) => renderInline(node, `${key}.${i}`, ctx));
}

function renderInline(node: Inline, key: string, ctx: Ctx): ReactNode {
  if (node.type === "text" || !ctx.inlineAt?.size) {
    return renderNode(node, key, ctx);
  }
  // A marker never sits inside a link, code or a container's own markup: it follows the node.
  const after: ReactNode[] = [];
  for (const [at, value] of ctx.inlineAt) {
    if (at > node.start && at <= node.end) {
      after.push(<Fragment key={`${key}@${at}`}>{value}</Fragment>);
    }
  }
  const inner =
    node.type === "strong" || node.type === "em"
      ? renderNode(node, key, ctx)
      : renderNode(node, key, { ...ctx, inlineAt: undefined });
  if (node.type === "strong" || node.type === "em" || after.length === 0) {
    return inner;
  }
  return (
    <Fragment key={key}>
      {inner}
      {after}
    </Fragment>
  );
}

function renderNode(node: Inline, key: string, ctx: Ctx): ReactNode {
  switch (node.type) {
    case "text":
      return <Fragment key={key}>{decorate(node, key, ctx)}</Fragment>;
    case "break":
      return <br key={key} />;
    case "strong":
      return <strong key={key}>{inlines(node.children, key, ctx)}</strong>;
    case "em":
      return <em key={key}>{inlines(node.children, key, ctx)}</em>;
    case "code":
      return (
        <code key={key} className="rounded-sm bg-surface-sunken px-1 py-px font-mono text-[0.85em]">
          {node.text}
        </code>
      );
    case "atom":
      return ctx.renderAtom ? (
        <Fragment key={key}>{ctx.renderAtom(node, key)}</Fragment>
      ) : (
        <Fragment key={key}>{node.raw}</Fragment>
      );
    case "wikilink": {
      const href = ctx.resolve?.(node.title) ?? null;
      return href ? (
        <Link
          key={key}
          href={href}
          className="font-medium text-accent-text underline-offset-4 hover:underline"
          {...(ctx.openWikiLink && {
            onClick: (event: MouseEvent) => {
              if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
                event.preventDefault();
                ctx.openWikiLink?.(href);
              }
            },
          })}
        >
          {node.label}
        </Link>
      ) : (
        <span key={key} className="text-fg-secondary">
          {node.label}
        </span>
      );
    }
    case "link":
      return node.href ? (
        <a
          key={key}
          href={node.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-describedby={ctx.newTab}
          className="text-accent-text underline underline-offset-4"
        >
          {inlines(node.children, key, ctx)}
          {ctx.showLinkHost ? <LinkHost href={node.href} label={plainText(node.children)} /> : null}
        </a>
      ) : (
        <Fragment key={key}>{inlines(node.children, key, ctx)}</Fragment>
      );
  }
}

/** The first heading only; its original index remains the TOC anchor on the article title. */
export function titleHeadingIndex(
  blocks: readonly Block[],
  title: string | undefined,
): number | null {
  if (!title) {
    return null;
  }
  const normalized = (value: string) => value.trim().replace(/\s+/gu, " ").toLowerCase();
  const index = blocks.findIndex((block) => block.type === "heading");
  const first = blocks[index];
  return first?.type === "heading" && normalized(plainText(first.content)) === normalized(title)
    ? index
    : null;
}

/** The host (or mail address) a link goes to, unless its label already names it. */
function LinkHost({ href, label }: { href: string; label: string }) {
  let target: string;
  try {
    const url = new URL(href);
    target = url.protocol === "mailto:" ? url.pathname : url.host;
  } catch {
    return null;
  }
  if (!target || labelHost(label) === target.toLowerCase()) {
    return null;
  }
  return <span className="text-fg-muted no-underline"> ({target})</span>;
}

/** The host a label names, if the whole label is a host, URL or mail address. */
function labelHost(label: string): string | null {
  const text = label.trim().toLowerCase();
  if (/\s/.test(text) || text === "") {
    return null;
  }
  if (text.startsWith("mailto:")) {
    return text.slice("mailto:".length);
  }
  if (/^[^@/]+@[^@/]+$/.test(text)) {
    return text;
  }
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//.test(text) ? text : `https://${text}`).host;
  } catch {
    return null;
  }
}

/** A text leaf cut at the decorations' edges, each decorated piece wrapped by the caller. */
function decorate(node: Inline & { type: "text" }, key: string, ctx: Ctx): ReactNode {
  const decorations = ctx.decorations ?? [];
  const inserts = [...(ctx.inlineAt ?? [])].filter(([at]) => at > node.start && at <= node.end);
  if ((decorations.length === 0 || !ctx.renderDecoration) && inserts.length === 0) {
    return node.text;
  }
  const cuts = new Set([node.start, node.end]);
  for (const d of decorations) {
    for (const at of [d.start, d.end]) {
      if (at > node.start && at < node.end) {
        cuts.add(at);
      }
    }
  }
  for (const [at] of inserts) {
    cuts.add(at);
  }
  const points = [...cuts].sort((a, b) => a - b);
  const out: ReactNode[] = [];
  for (let p = 0; p < points.length - 1; p++) {
    const from = points[p] as number;
    const to = points[p + 1] as number;
    const piece = node.text.slice(from - node.start, to - node.start);
    const index = decorations.findIndex((d) => d.start <= from && to <= d.end);
    const d = decorations[index];
    out.push(
      d && ctx.renderDecoration
        ? ctx.renderDecoration(d.kind, piece, `${key}-${p}`, ctx.lastPiece.get(index) === to)
        : piece,
    );
    const insert = ctx.inlineAt?.get(to);
    if (insert !== undefined && inserts.length) {
      out.push(<Fragment key={`${key}@${to}`}>{insert}</Fragment>);
    }
  }
  return out;
}

/** For each decoration, the raw end of its last text piece in document order. */
function lastPieces(blocks: readonly Block[], decorations: readonly Decoration[]) {
  const last = new Map<number, number>();
  if (decorations.length === 0) {
    return last;
  }
  const visit = (nodes: readonly Inline[]) => {
    for (const node of nodes) {
      if (node.type === "text") {
        decorations.forEach((d, index) => {
          if (node.start < d.end && d.start < node.end) {
            last.set(index, Math.min(node.end, d.end));
          }
        });
      } else if ("children" in node) {
        visit(node.children);
      }
    }
  };
  const walk = (block: Block) => {
    switch (block.type) {
      case "heading":
      case "paragraph":
      case "quote":
        visit(block.content);
        break;
      case "list":
        for (const item of block.items) {
          visit(item.content);
          item.children.forEach(walk);
        }
        break;
      case "table":
        for (const cell of block.header) {
          visit(cell);
        }
        for (const row of block.rows) {
          for (const cell of row) {
            visit(cell);
          }
        }
        break;
    }
  };
  blocks.forEach(walk);
  return last;
}
